import { useMemo } from "react";
import { z } from "zod";
import { compactNumber, type WidgetSettingField, type WidgetSettingOption } from "@salestrak/ui";
import { currencySymbol, formatMoney, formatNumberValue } from "@salestrak/custom-fields";
import {
  FIELD_GROUP_DATE_TYPES,
  FIELD_GROUP_TYPES,
  MEASURABLE_FIELD_TYPES,
  MEASURE_FNS,
  fieldGroupToken,
  fieldTypeHeading,
  isMeasureFn,
  parseFieldGroupToken,
  type MeasureFn,
} from "@salestrak/widgets";
import { useWorkspaceData, type WorkspaceCustomField } from "#app/lib/data/workspace-data-provider";

/**
 * What a chart's figures ARE, and the settings rows that choose it. A count
 * widget's Data section reads: a **Measure** select whose list is the project
 * count followed by every number and money field the workspace defines, under
 * the type headings — a field is picked directly, never through a "Custom
 * field" door and a second row. Once a field is the measure a **Calculate**
 * control (sum / average / lowest / highest) follows it, and on the charts
 * that can draw several series (the bar, the line) a **Compare with**
 * multiselect adds further fields beside it. The keys are `measure` (the count
 * choice or a field id), `calculate` and `compareFields`; `useFieldMeasures`
 * folds them into the `measures` argument `issueGroupCounts` takes, empty for
 * the count. The grammar (which types measure, which aggregates exist) is the
 * shared `@salestrak/widgets` vocabulary the server validates against.
 *
 * Group by reads the same way: the built-in dimensions, then every groupable
 * field, stored as the field's own `values.<fieldId>` token
 * (`use-dimension-counts.ts`).
 */

/** The Measure select's count choice. */
export const COUNT_MEASURE = "count";

/** A count widget's Measure: the count, or a custom field's id. */
export const measureSchema = z.string().default(COUNT_MEASURE);
/** The aggregate. Sum is the default: a money field's natural reading. */
export const calculateSchema = z.enum(MEASURE_FNS).default("sum");
/** Further measured fields drawn beside the Measure field, in pick order. */
export const compareFieldsSchema = z.array(z.string()).default([]);

/** The server-side measure a card asks for, or null for the plain count. */
export type FieldMeasure = { fieldId: string; fn: MeasureFn };

/**
 * The field id a Measure value names, or null for a count choice. `counts` is
 * the widget's own count vocabulary (`COUNT_MEASURE` for the charts; the
 * metric's two counts) — anything outside it is a field id.
 */
export function measureFieldId(
  measure: string | null | undefined,
  counts: readonly string[] = [COUNT_MEASURE],
): string | null {
  if (!measure || counts.includes(measure)) return null;
  return measure;
}

/** Fold the Measure keys into the asks: the Measure field first, then each
 *  Compare with field once (the Measure field never repeats). Empty for a count. */
export function fieldMeasures(
  fieldId: string | null,
  fn: string | null | undefined,
  compare?: readonly string[] | null,
): FieldMeasure[] {
  if (!fieldId) return [];
  const aggregate = isMeasureFn(fn) ? fn : "sum";
  const ids = [fieldId];
  for (const id of compare ?? []) if (!ids.includes(id)) ids.push(id);
  return ids.map((id) => ({ fieldId: id, fn: aggregate }));
}

/**
 * The asks a widget's Measure keys resolve to against THIS workspace's fields,
 * memoized on the stored values so a reader's query variables hold still. A
 * field the workspace no longer has drops out of the asks; when the Measure
 * field itself is gone the card has nothing to plot and `pending` says so
 * (the card shows `MISSING_FIELD_TITLE`).
 */
export function useFieldMeasures(
  measure: string | null | undefined,
  fn: string | null | undefined,
  compare?: readonly string[] | null,
  counts: readonly string[] = [COUNT_MEASURE],
): { asked: FieldMeasure[]; pending: boolean } {
  const { customFields } = useWorkspaceData();
  const fieldId = measureFieldId(measure, counts);
  // The ids are a stored array, so their serialization is the stable identity.
  const compareKey = (compare ?? []).join("|");
  return useMemo(() => {
    if (!fieldId) return { asked: [], pending: false };
    const known = new Set(customFields.filter(isMeasurableField).map((field) => field.customFieldId));
    if (!known.has(fieldId)) return { asked: [], pending: true };
    const extra = compareKey ? compareKey.split("|").filter((id) => known.has(id)) : [];
    return { asked: fieldMeasures(fieldId, fn, extra), pending: false };
  }, [fieldId, fn, compareKey, customFields]);
}

/** The aggregate's word in a picker, a caption or a derived title. */
export const MEASURE_FN_LABEL: Record<MeasureFn, string> = {
  sum: "Sum",
  avg: "Average",
  min: "Lowest",
  max: "Highest",
};

/** "Sum of Contract value", "Average Contract value", "Lowest Contract value". */
export function measureLabel(fn: MeasureFn, fieldName: string): string {
  return fn === "sum" ? `Sum of ${fieldName}` : `${MEASURE_FN_LABEL[fn]} ${fieldName}`;
}

/** The Measure select's `dynamicOptions` source name — every measurable workspace field. */
export const MEASURE_FIELDS_SOURCE = "measureFields";
/** The Group by select's `dynamicOptions` source name — every groupable field. */
export const DIMENSION_FIELDS_SOURCE = "dimensionFields";

/** What a Group by or Measure trigger reads when its stored field is gone
 *  from the workspace (deleted or archived) — the same words the card's empty
 *  state leads with. */
export const MISSING_FIELD_LABEL = "Field no longer available";

/** The Measure select of a count widget: the count, then the workspace's
 *  number and money fields under their type headings. Searchable — a
 *  workspace's field list is open-ended. */
export const MEASURE_SETTING_FIELD: WidgetSettingField = {
  key: "measure",
  kind: "select",
  label: "Measure",
  icon: "hash",
  searchable: true,
  missingLabel: MISSING_FIELD_LABEL,
  options: [{ value: COUNT_MEASURE, label: "Count of Projects" }],
  dynamicOptions: MEASURE_FIELDS_SOURCE,
};

/** The Calculate row a field Measure reveals — the aggregate over the field,
 *  a select in the same row grammar as the Measure above it. */
export const CALCULATE_SETTING_FIELD: WidgetSettingField = {
  key: "calculate",
  kind: "select",
  label: "Calculate",
  icon: "calculator",
  options: MEASURE_FNS.map((fn) => ({ value: fn, label: MEASURE_FN_LABEL[fn] })),
};

/** The Compare with row a field Measure reveals on a chart that draws several
 *  series: further measurable fields, each its own bar or line beside the
 *  Measure field. The same row grammar as every other select, opening a
 *  multiple-selection popup grouped by type heading. */
export const COMPARE_FIELDS_SETTING_FIELD: WidgetSettingField = {
  key: "compareFields",
  kind: "multiselect",
  label: "Compare with",
  icon: "columns",
  placeholder: "None",
  emptyLabel: "No number or money fields",
  dynamicOptions: MEASURE_FIELDS_SOURCE,
};

/**
 * A widget's Measure rows for its current config: the select alone while it
 * counts; the select plus Calculate once a field is the measure; plus Compare
 * with when the chart draws several series (`compare`). `switchField` is the
 * widget's own Measure select (the metric card's carries two counts).
 */
export function measureSettingFields(
  switchField: WidgetSettingField,
  fieldChosen: boolean,
  compare = false,
): WidgetSettingField[] {
  if (!fieldChosen) return [switchField];
  return compare
    ? [switchField, CALCULATE_SETTING_FIELD, COMPARE_FIELDS_SETTING_FIELD]
    : [switchField, CALCULATE_SETTING_FIELD];
}

/** The choice older configs stored to mean "a custom field, named in a second
 *  key" — `groupBy: "field"` + `groupField`, `measure: "field"` + `measureFields`.
 *  Read only by {@link withLegacyDataKeys}; nothing writes it. */
const LEGACY_FIELD_CHOICE = "field";

/**
 * Wrap a widget's config schema so a stored choice-then-field pair parses as
 * the flat keys: the Group by choice becomes the field's `values.<fieldId>`
 * token (or the schema default when no field was chosen), the Measure choice
 * becomes the first stored field id (the default count when none), and the
 * remaining stored fields become `compareFields` where the chart draws them
 * (`compare`). Every widget that carries the shared Data rows wraps its schema
 * here; the kit only ever calls `safeParse`, so the wrap is transparent to it.
 */
export function withLegacyDataKeys<S extends z.ZodType>(
  schema: S,
  keys: { groupBy?: string; measure?: string; compare?: boolean } = {},
) {
  const groupKey = keys.groupBy ?? "groupBy";
  const measureKey = keys.measure ?? "measure";
  return z.preprocess((raw) => {
    if (!raw || typeof raw !== "object") return raw;
    const config = { ...(raw as Record<string, unknown>) };
    if (config[groupKey] === LEGACY_FIELD_CHOICE) {
      const groupField = config.groupField;
      config[groupKey] = typeof groupField === "string" && groupField ? fieldGroupToken(groupField) : undefined;
    }
    if (config[measureKey] === LEGACY_FIELD_CHOICE) {
      const fields = Array.isArray(config.measureFields)
        ? config.measureFields.filter((id): id is string => typeof id === "string")
        : [];
      config[measureKey] = fields[0];
      if (keys.compare && fields.length > 1 && config.compareFields === undefined) {
        config.compareFields = fields.slice(1);
      }
    }
    return config;
  }, schema);
}

export function isMeasurableField(field: { type: string }): boolean {
  return (MEASURABLE_FIELD_TYPES as readonly string[]).includes(field.type);
}

export function isGroupableField(field: { type: string }): boolean {
  return (FIELD_GROUP_TYPES as readonly string[]).includes(field.type);
}

export function isDateField(field: { type: string }): boolean {
  return (FIELD_GROUP_DATE_TYPES as readonly string[]).includes(field.type);
}

/** Why a field the picker lists cannot be picked: the reason sits under its
 *  name, so a user looking for "Roof type" finds it and learns why it is grey
 *  rather than concluding the field is missing. */
const UNMEASURABLE_REASON = "Only number and money fields can be measured";

/** Every workspace field as a picker row — the usable ones under their type
 *  heading, the rest greyed with the reason — so the list is the workspace's
 *  whole vocabulary and a field is never silently absent. Usable rows come
 *  first so the pickable set is not buried under the explanations. */
function fieldOptions(
  customFields: readonly WorkspaceCustomField[],
  usable: (field: WorkspaceCustomField) => boolean,
  valueOf: (field: WorkspaceCustomField) => string,
  reason: (field: WorkspaceCustomField) => string,
): WidgetSettingOption[] {
  const row = (field: WorkspaceCustomField, disabled: boolean): WidgetSettingOption => ({
    value: valueOf(field),
    label: field.name,
    group: fieldTypeHeading(field.type),
    ...(disabled ? { disabled: true, description: reason(field) } : {}),
  });
  return [
    ...customFields.filter(usable).map((field) => row(field, false)),
    ...customFields.filter((field) => !usable(field)).map((field) => row(field, true)),
  ];
}

/** The Measure select's field options: the workspace's money and number fields
 *  by heading, each valued by its id; every other field greyed with why. */
export function useMeasureFieldOptions(): WidgetSettingOption[] {
  const { customFields } = useWorkspaceData();
  return useMemo(
    () =>
      fieldOptions(
        customFields,
        isMeasurableField,
        (field) => field.customFieldId,
        () => UNMEASURABLE_REASON,
      ),
    [customFields],
  );
}

/** The Group by select's field options: every groupable field, by type heading,
 *  each valued by its `values.<fieldId>` token, and every other field greyed
 *  with why. A date field buckets by month — the one grain a categorical chart
 *  can hold; the line chart owns finer time axes. The option does not say so:
 *  every date field reads the same way, so the caption under each one only
 *  repeats the heading. */
export function useDimensionFieldOptions(): WidgetSettingOption[] {
  const { customFields } = useWorkspaceData();
  return useMemo(
    () =>
      fieldOptions(
        customFields,
        isGroupableField,
        (field) => fieldGroupToken(field.customFieldId),
        (field) => `${fieldTypeHeading(field.type)} fields can't be grouped`,
      ),
    [customFields],
  );
}

/**
 * The name a stored Group by token or Measure id refers to — what a derived
 * title prints ("Deductible by Bid Type"). Keyed by option source, the shape
 * `WidgetControls.optionLabels` takes; a grained token (`values.<id>.month`)
 * names the same field as its grainless option. Undefined for a built-in
 * choice, the count, or a field the workspace no longer has.
 */
export function useFieldOptionLabels(): Record<string, (value: string) => string | undefined> {
  const { customFields } = useWorkspaceData();
  return useMemo(() => {
    const byId = new Map(customFields.map((field) => [field.customFieldId, field.name]));
    return {
      [DIMENSION_FIELDS_SOURCE]: (value: string) => {
        const token = parseFieldGroupToken(value);
        return token ? byId.get(token.fieldId) : undefined;
      },
      [MEASURE_FIELDS_SOURCE]: (value: string) => byId.get(value),
    };
  }, [customFields]);
}

/**
 * Fold several buckets' figures into one — the stage-category fold, the
 * unresolved-id fold and the "Other" tail all merge buckets, and only a count
 * or a sum merges by adding. An average re-weights by each bucket's project
 * count; a lowest/highest takes the extreme. `fn` null is the plain count.
 * Null when no part carries a value (a field nobody filled in).
 */
export function foldMeasure(
  fn: MeasureFn | null,
  parts: readonly { total: number; count?: number; value?: number | null }[],
): number | null {
  if (fn === null) return parts.reduce((sum, part) => sum + part.total, 0);
  const valued = parts.filter((part) => part.value != null);
  if (valued.length === 0) return null;
  const values = valued.map((part) => part.value as number);
  switch (fn) {
    case "sum":
      return values.reduce((sum, value) => sum + value, 0);
    case "avg": {
      // A bucket with no count is a legacy slice that never carried one; it
      // weighs as one project, which is the only honest weight left.
      const weight = valued.reduce((sum, part) => sum + (part.count ?? 1), 0);
      const weighted = valued.reduce(
        (sum, part) => sum + (part.value as number) * (part.count ?? 1),
        0,
      );
      return weight > 0 ? weighted / weight : null;
    }
    case "min":
      return Math.min(...values);
    case "max":
      return Math.max(...values);
  }
}

/** How a widget prints its figures for the measure it carries. */
export type MeasureFormat = {
  /** The measure asked for, null for the count. Under several fields, the first. */
  measure: FieldMeasure | null;
  /** The measured field, when the workspace still has it. Under several, the first. */
  field: WorkspaceCustomField | null;
  /** "Projects" for the count; "Sum of Contract value" for a field. */
  label: string;
  /** The muted caption under a big number: "Projects", "sum of Contract value". */
  caption: string;
  /** Full precision — a tooltip, a legend value, a big number. */
  format: (value: number) => string;
  /** Abbreviated — an axis tick, a bar's value label. */
  compact: (value: number) => string;
  /** A tooltip line: the figure, plus the project count behind a measured one. */
  detail: (value: number, count: number) => string;
  /** The projects behind a figure, as words: "12 projects". */
  projects: (count: number) => string;
};

const projectsNoun = (count: number) => `${count.toLocaleString()} ${count === 1 ? "Project" : "Projects"}`;

const COUNT_FORMAT: MeasureFormat = {
  measure: null,
  field: null,
  label: "Projects",
  caption: "Projects",
  format: (value) => value.toLocaleString(),
  compact: compactNumber,
  detail: (value) => projectsNoun(value),
  projects: projectsNoun,
};

function fieldFormat(measure: FieldMeasure, field: WorkspaceCustomField): MeasureFormat {
  const money = field.type === "money";
  const currency = field.config?.currency ?? null;
  const format = (value: number) =>
    money ? formatMoney(value, currency) : formatNumberValue(value, field.config);
  const compact = (value: number) =>
    money ? `${currencySymbol(currency)}${compactNumber(value)}` : compactNumber(value);
  const label = measureLabel(measure.fn, field.name);
  return {
    measure,
    field,
    label,
    caption: label.charAt(0).toLowerCase() + label.slice(1),
    format,
    compact,
    detail: (value, count) => `${format(value)} · ${projectsNoun(count)}`,
    projects: projectsNoun,
  };
}

/**
 * One format per asked measure, in order — a bar's or a line's series each
 * print their own field. A measure whose field the workspace no longer has
 * drops out, so the list can be shorter than the ask; `measures` on the
 * returned entries says which survived.
 */
export function useMeasureFormats(measures: readonly FieldMeasure[]): MeasureFormat[] {
  const { customFields } = useWorkspaceData();
  return useMemo(
    () =>
      measures.flatMap((measure) => {
        const field = customFields.find((candidate) => candidate.customFieldId === measure.fieldId);
        return field ? [fieldFormat(measure, field)] : [];
      }),
    [measures, customFields],
  );
}

/**
 * The ONE format a card prints its whole through: the count's when nothing is
 * measured; the field's when one is; the FIRST field's when several are — a
 * figure summed across fields only reads if they share a unit, and the first
 * field's currency and decimals are the best statement of that unit. Its label
 * then names every field ("Sum of Contract value + Deposit collected").
 */
export function useMeasureFormat(measures: readonly FieldMeasure[]): MeasureFormat {
  const formats = useMeasureFormats(measures);
  return useMemo(() => {
    const first = formats[0];
    if (!first) return COUNT_FORMAT;
    if (formats.length === 1) return first;
    const names = formats.map((format) => format.field?.name ?? "").join(" + ");
    const label = measureLabel(first.measure?.fn ?? "sum", names);
    return { ...first, label, caption: label.charAt(0).toLowerCase() + label.slice(1) };
  }, [formats]);
}
